import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { io as socketIO } from 'socket.io-client';
import QRCode from 'react-qr-code';
import { lastUsernameQuery } from '../utils/auth';
import { AUTH_URL } from '../utils/authApi';
import { isMobileDevice, isWebAuthnSupported, platformBioDefinitiefAfwezig } from '../utils/deviceAuth';
import { geenVerbinding, GEEN_VERBINDING } from '../utils/verbinding';
import { makePkcePair } from '../utils/pkce';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8009';
// Laravel is the only place that decides who someone is. In production it sits
// behind the same nginx as this app, so the default is same-origin.
// The QR lives as long as Laravel keeps the session (`expires_in`, 300 s), minus a margin to
// claim in: at 60 s a phone that approved the old QR after a refresh approved a session the pc
// no longer held the verifier for (review 25-10-2026). This is the fallback without `expires_in`.
const QR_TIMEOUT_SECONDS = 270;
const QR_CLAIM_MARGIN_SECONDS = 30;
// Auto-refreshes before the QR waits for a click: an open login page must not
// keep asking Laravel for codes -- the QR shares the per-IP auth limit (30/hour)
// with the magic link.
const QR_MAX_AUTO_REFRESH = 5;

function getDeviceInfo() {
  const ua = navigator.userAgent;
  let browser = 'Unknown Browser';
  let os = 'Unknown OS';
  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  return { browser, os, device_name: `${browser} on ${os}` };
}

function base64urlToBuffer(base64url) {
  const padding = '='.repeat((4 - base64url.length % 4) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  const rawData = atob(base64);
  return Uint8Array.from(rawData, c => c.charCodeAt(0)).buffer;
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (const byte of bytes) str += String.fromCharCode(byte);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}


// ============================================================
// Main Login Component — device-aware
// ============================================================

export function Login({ onLogin }) {
  const isMobile = isMobileDevice();

  // Geen wachtwoord, ook geen terugval: ADR-005 (geen wachtwoorden in enig project). De
  // `?fallback=1`-route naar de Node-login is 25-10-2026 weg; de noodingang is de herstelcode.

  // Smartphone: de bio-knop staat BOVEN een altijd zichtbaar e-mailveld
  // (kb/patterns/havun-mobile-login.md regel 3) -- geen tabs, geen wisselknop.
  // Wie geen vingerafdruk kan geven, hoeft niets te ontdekken. Desktop: QR-code
  // bovenaan, magic link eronder (recept regel 4). QR loopt via Laravel met PKCE
  // (25-10-2026): de pc haalt zijn token zelf op met een verifier die alleen hij kent.
  // NOOIT QR op een smartphone (regel 2).
  const [view, setView] = useState('default');

  // De bio-knop staat er direct op een smartphone met WebAuthn (regel 5, Henk
  // 19-10-2026): een koud opstartende PWA haalde de 2s-detectie niet en de knop
  // verdween. Alleen een definitief nee haalt hem weg -- traag of fout niet.
  // Het e-mailveld staat er hoe dan ook, dus een leeg scherm bestaat niet.
  const [biometricAvailable, setBiometricAvailable] = useState(
    isMobile && isWebAuthnSupported(),
  );

  useEffect(() => {
    if (!biometricAvailable) return;
    let afgebroken = false;
    const verberg = () => { if (!afgebroken) setBiometricAvailable(false); };

    (async () => {
      // Eerst het toestel zelf: geen platform-authenticator, geen serveraanvraag.
      if (!(await platformBioDefinitiefAfwezig())) {
        try {
          // Met de laatst-ingelogde gebruiker checkt de backend diens credentials;
          // zonder username antwoordt hij of er überhaupt passkeys bestaan
          // (discoverable-login).
          const response = await axios.get(`${AUTH_URL}/api/auth/webauthn/available${lastUsernameQuery()}`);
          if (response?.data?.available === false) verberg();
        } catch {
          // Geen antwoord is geen nee: de knop blijft.
        }
        return;
      }
      verberg();
    })();

    return () => { afgebroken = true; };
    // Bewust alleen bij mount: de beginwaarde komt uit device- en URL-eigenschappen
    // die tijdens een sessie niet veranderen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (token, user) => {
    onLogin(token, user);
  };

  return (
    <LoginShell>
      {view === 'recovery' && (
        <RecoveryCodePanel onLogin={handleLogin} onBack={() => setView('default')} />
      )}

      {view === 'default' && (
        <>
          {/* Recept regel 4 (Henk 19-10-2026): op desktop staat de QR-code
              bovenaan, direct zichtbaar -- achter een knop onderaan werd hij
              gemist. Regel 2 is absoluut: een smartphone toont NOOIT QR, ook
              niet als de bio-detectie faalde. `havun-mobile-login.md`. */}
          {!isMobile && (
            <>
              <QrLoginPanel onLogin={handleLogin} />
              <Divider />
            </>
          )}
          {biometricAvailable && (
            <>
              <BiometricLogin onLogin={handleLogin} />
              <Divider />
            </>
          )}
          <MagicLinkPanel onRecovery={() => setView('recovery')} />
        </>
      )}
    </LoginShell>
  );
}

// ============================================================
// Shared components
// ============================================================

function LoginShell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-md w-full mx-4">
        <div className="card dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">HavunCore</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">Beheer Dashboard</p>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

function Divider() {
  return (
    <div className="my-5 flex items-center">
      <div className="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
      <span className="px-4 text-sm text-gray-400">of</span>
      <div className="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
    </div>
  );
}

function ErrorMessage({ message }) {
  if (!message) return null;
  return (
    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
      {message}
    </div>
  );
}

function SuccessMessage({ message }) {
  if (!message) return null;
  return (
    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded-lg text-sm">
      {message}
    </div>
  );
}

// ============================================================
// Magic link — asks Laravel, which owns the accounts
// ============================================================

function MagicLinkPanel({ onRecovery }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await axios.post(`${AUTH_URL}/api/auth/magic-link`, {
        email,
        site_name: 'HavunCore',
        // Where the finished login returns to. Laravel checks this against its
        // allowlist and drops anything unlisted, so a wrong origin means no
        // handover -- never a redirect somewhere else.
        callback_url: `${window.location.origin}/`,
      });
      // The answer is the same for a known and an unknown address; showing
      // anything else would turn this form into an account-finder.
      setSent(true);
    } catch (err) {
      setError(geenVerbinding(err) ? GEEN_VERBINDING : err.response?.status === 429
        ? 'Te veel aanvragen. Wacht een minuut.'
        : 'Aanvragen is niet gelukt.');
    } finally {
      setIsLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-5">
        <SuccessMessage message="Als dit adres bekend is, staat er een inloglink in je mailbox. De link is 15 minuten geldig." />
        <button type="button" onClick={onRecovery} className="w-full btn-secondary">
          Geen mail ontvangen? Gebruik een herstelcode
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && <ErrorMessage message={error} />}
      <div>
        <label htmlFor="magic-email" className="block text-sm font-medium text-slate-300 mb-2">E-mailadres</label>
        <input
          id="magic-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input-field"
          placeholder="jij@havun.nl"
          autoComplete="email"
          required
        />
      </div>
      <button type="submit" disabled={isLoading} className="w-full btn-primary">
        {isLoading ? 'Versturen...' : 'Stuur inloglink'}
      </button>
      <button type="button" onClick={onRecovery} className="w-full btn-secondary">
        Geen mail? Gebruik een herstelcode
      </button>
    </form>
  );
}

// ============================================================
// Recovery code — the way in when mail is down
// ============================================================

function RecoveryCodePanel({ onLogin, onBack }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${AUTH_URL}/api/auth/recovery-code`, {
        email,
        code: code.trim(),
        browser: navigator.userAgent.includes('Chrome') ? 'Chrome' : 'Browser',
        os: navigator.platform || 'Onbekend',
      });

      if (response.data.success) {
        onLogin(response.data.device_token, response.data.user);
      } else {
        setError('Deze code werkt niet.');
      }
    } catch (err) {
      setError(geenVerbinding(err) ? GEEN_VERBINDING : err.response?.status === 429
        ? 'Te veel pogingen. Wacht een minuut.'
        : 'Deze code werkt niet.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && <ErrorMessage message={error} />}
      <p className="text-sm text-slate-400">
        Eén van de tien codes van je geprinte vel. Elke code werkt één keer.
      </p>
      <div>
        <label htmlFor="recovery-email" className="block text-sm font-medium text-slate-300 mb-2">E-mailadres</label>
        <input
          id="recovery-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input-field"
          placeholder="jij@havun.nl"
          autoComplete="email"
          required
        />
      </div>
      <div>
        <label htmlFor="recovery-code" className="block text-sm font-medium text-slate-300 mb-2">Herstelcode</label>
        <input
          id="recovery-code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="input-field"
          autoComplete="one-time-code"
          spellCheck="false"
          required
        />
      </div>
      <button type="submit" disabled={isLoading} className="w-full btn-primary">
        {isLoading ? 'Controleren...' : 'Inloggen'}
      </button>
      <button type="button" onClick={onBack} className="w-full btn-secondary">
        Terug
      </button>
    </form>
  );
}

// ============================================================
// Biometric Login (smartphone only)
// ============================================================

function BiometricLogin({ onLogin }) {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleBiometric = async () => {
    setError('');
    setIsLoading(true);

    try {
      // Met de laatst-ingelogde gebruiker vult de backend allowCredentials;
      // zonder username werken alleen discoverable (resident) passkeys.
      const optionsResponse = await axios.get(`${AUTH_URL}/api/auth/webauthn/login-options${lastUsernameQuery()}`);
      const options = optionsResponse.data;

      const publicKeyOptions = {
        challenge: base64urlToBuffer(options.challenge),
        timeout: options.timeout,
        rpId: options.rpId,
        userVerification: options.userVerification,
      };

      if (options.allowCredentials) {
        publicKeyOptions.allowCredentials = options.allowCredentials.map(c => ({
          id: base64urlToBuffer(c.id),
          type: c.type,
          transports: c.transports,
        }));
      }

      const credential = await navigator.credentials.get({ publicKey: publicKeyOptions });

      const loginResponse = await axios.post(`${AUTH_URL}/api/auth/webauthn/login`, {
        credential: {
          id: credential.id,
          rawId: bufferToBase64url(credential.rawId),
          type: credential.type,
          response: {
            authenticatorData: bufferToBase64url(credential.response.authenticatorData),
            clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
            signature: bufferToBase64url(credential.response.signature),
          },
        },
      });

      if (loginResponse.data.success) {
        onLogin(loginResponse.data.device_token, loginResponse.data.user);
      }
    } catch (err) {
      if (geenVerbinding(err)) {
        setError(GEEN_VERBINDING);
      } else if (err.name === 'NotAllowedError') {
        setError('Vingerafdruk niet gelukt of geannuleerd. Gebruik de inloglink hieronder.');
      } else {
        setError('Biometrische login mislukt');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Regel 6: een mislukte ceremonie leidt nergens heen -- het e-mailveld
          eronder blijft staan, dus de melding is genoeg. */}
      <ErrorMessage message={error} />

      <button
        onClick={handleBiometric}
        disabled={isLoading}
        className="w-full btn-primary py-4 flex items-center justify-center gap-3 text-lg disabled:opacity-50"
      >
        {isLoading ? 'Verificatie...' : (
          <><span className="text-2xl">🔐</span> Inloggen met biometric</>
        )}
      </button>
    </div>
  );
}

// ============================================================
// QR Login Panel (desktop only)
// ============================================================

function QrLoginPanel({ onLogin }) {
  const [qrValue, setQrValue] = useState('');
  const [status, setStatus] = useState('generating');
  const [error, setError] = useState('');
  const [expiresIn, setExpiresIn] = useState(QR_TIMEOUT_SECONDS);
  const qrLifetime = useRef(QR_TIMEOUT_SECONDS);
  const socketRef = useRef(null);
  const expiryInterval = useRef(null);
  const isGenerating = useRef(false);
  const autoRefreshes = useRef(0);
  // PKCE verifier of the current QR: memory only, never localStorage. A reload means a new QR.
  const verifierRef = useRef(null);

  useEffect(() => {
    generateQr();

    return cleanup;
    // Bewust alleen bij mount: elke aanroep vraagt een nieuwe QR-sessie aan de server.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanup = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    clearInterval(expiryInterval.current);
    isGenerating.current = false;
  };

  const generateQr = async () => {
    if (isGenerating.current) return;
    isGenerating.current = true;

    cleanup();
    setStatus('generating');
    setError('');

    try {
      const deviceInfo = getDeviceInfo();
      // Laravel bezit de accounts (AUTH_MODE=laravel). De Node-backend heeft nog eigen
      // QR-routes staan, maar die deelden een token uit dat Laravel weigert.
      const { verifier, challenge } = await makePkcePair();
      verifierRef.current = verifier;
      const response = await axios.post(`${AUTH_URL}/api/auth/qr/generate`, { ...deviceInfo, code_challenge: challenge });

      if (response.data.success) {
        const code = response.data.qr_code;
        // Laravel sends only the code; the phone camera opens /qr/<code> (QrApprove).
        setQrValue(response.data.qr_value || `${window.location.origin}/qr/${code}`);
        setStatus('pending');
        const serverSeconds = Number(response.data.expires_in);
        qrLifetime.current = serverSeconds > 0
          ? Math.max(30, serverSeconds - QR_CLAIM_MARGIN_SECONDS)
          : QR_TIMEOUT_SECONDS;
        setExpiresIn(qrLifetime.current);
        connectSocket(code);
        startCountdown();
      }
    } catch {
      setError('QR code genereren mislukt');
      setStatus('error');
    } finally {
      isGenerating.current = false;
    }
  };

  const connectSocket = (code) => {
    const socket = socketIO(API_URL, {
      auth: { qrCode: code },
      transports: ['websocket', 'polling'],
    });

    socket.on('qr:scanned', () => setStatus('scanned'));
    socket.on('qr:approved', async (data) => {
      // Laravel pushes this via POST /api/internal/notify. It carries only the code: anyone who
      // photographed the QR can join this room, so the push holds nothing usable. This pc
      // collects its token with the PKCE verifier only it knows (25-10-2026).
      if (data.code !== code || !verifierRef.current) return;

      setStatus('approved');
      cleanup();

      try {
        const res = await axios.post(`${AUTH_URL}/api/auth/qr/claim`, { qr_code: code, code_verifier: verifierRef.current });
        verifierRef.current = null;
        if (res.data?.success && res.data.device_token) {
          onLogin(res.data.device_token, res.data.user);

          return;
        }
        setError('Inloggen mislukt — vraag een nieuwe code aan.');
        setStatus('error');
      } catch {
        setError('Inloggen mislukt — vraag een nieuwe code aan.');
        setStatus('error');
      }
    });
    socket.on('connect_error', (err) => {
      console.warn('QR WebSocket connection error:', err.message);
    });

    socketRef.current = socket;
  };

  const startCountdown = () => {
    clearInterval(expiryInterval.current);
    setExpiresIn(qrLifetime.current);
    expiryInterval.current = setInterval(() => {
      setExpiresIn(prev => {
        if (prev <= 1) {
          clearInterval(expiryInterval.current);
          if (autoRefreshes.current < QR_MAX_AUTO_REFRESH) {
            autoRefreshes.current += 1;
            generateQr();
          } else {
            cleanup();
            setStatus('expired');
          }
          return qrLifetime.current;
        }
        return prev - 1;
      });
    }, 1000);
  };

  return (
    <div data-testid="qr-login" className="flex flex-col items-center space-y-4">
      {status === 'generating' ? (
        <div className="w-56 h-56 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-lg">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
        </div>
      ) : status === 'approved' ? (
        <div className="w-56 h-56 flex items-center justify-center bg-green-100 dark:bg-green-900/30 rounded-lg">
          <span className="text-5xl">✅</span>
        </div>
      ) : status === 'expired' ? (
        <div className="w-56 h-56 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-lg">
          <button
            type="button"
            onClick={() => { autoRefreshes.current = 0; generateQr(); }}
            className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm hover:bg-primary-600"
          >
            Nieuwe QR-code
          </button>
        </div>
      ) : qrValue ? (
        <div className="p-3 bg-white rounded-lg shadow-inner">
          <QRCode value={qrValue} size={200} level="M" />
        </div>
      ) : null}

      <p className={`text-sm font-medium ${
        status === 'scanned' ? 'text-primary-500' :
        status === 'approved' ? 'text-green-600' :
        status === 'error' ? 'text-red-600' :
        'text-gray-500 dark:text-gray-400'
      }`}>
        {status === 'generating' && 'QR code laden...'}
        {status === 'pending' && 'Scan met je telefoon'}
        {status === 'scanned' && 'Gescand! Wachten op goedkeuring...'}
        {status === 'approved' && 'Ingelogd!'}
        {status === 'error' && error}
        {status === 'expired' && 'QR-code verlopen'}
      </p>

      {(status === 'pending' || status === 'scanned') && (
        <p className="text-xs text-gray-400">Vernieuwt over {expiresIn}s</p>
      )}

      {status === 'error' && (
        <button onClick={generateQr} className="text-sm text-primary-500 hover:underline">
          Opnieuw proberen
        </button>
      )}
    </div>
  );
}

export { QrLoginPanel as QrLogin };
